import { VStack } from '@chakra-ui/react';
import { modulo } from '@utils/math.utils';
import { AngleUnits, formatUnit } from '@utils/unit.utils';
import { useIntl } from 'react-intl';

import { FocusModeEnum } from '@/types/sensor/sensors';
import { CameraStatus } from '@/types/sensor/status.types';

import CameraStatusValue from './CameraStatusValue';

type Props = {
  cameraStatus: CameraStatus;
  cameraId?: number;
  maintenance?: boolean;
};

export function CameraStatusInfo({ cameraStatus, cameraId, maintenance = false }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const videoStatus = cameraStatus.videoStatuses.find((vs) => vs.id === cameraId);

  return (
    <VStack padding={4} paddingTop={8} gap={1}>
      <CameraStatusValue
        label={formatMessage({ id: 'cameras.status' })}
        value={
          maintenance
            ? formatMessage({ id: 'sensors.maintenance' })
            : formatMessage({
                id: `sensors.status.${cameraStatus.effectiveStatus}`,
              })
        }
      />
      <CameraStatusValue
        label={formatMessage({ id: 'cameras.azimuth' })}
        value={formatUnit(AngleUnits.TURN, cameraStatus.pan && modulo(cameraStatus.pan, 360), { precision: 2 })}
      />
      <CameraStatusValue
        label={formatMessage({ id: 'cameras.site' })}
        value={formatUnit(AngleUnits.TURN, cameraStatus.tilt, { showPlusSign: true, precision: 2 })}
      />
      <CameraStatusValue
        label={formatMessage({ id: 'cameras.fov' })}
        value={formatUnit(AngleUnits.TURN, videoStatus?.fov, { precision: 2 })}
      />
      <CameraStatusValue
        label={formatMessage({ id: 'cameras.zoom' })}
        value={videoStatus?.magnification ? `x${videoStatus.magnification}` : formatMessage({ id: 'global.NA' })}
      />
      <CameraStatusValue
        label={formatMessage({ id: 'cameras.focus' })}
        value={videoStatus?.focusMode === FocusModeEnum.AUTO ? 'AUTOFOCUS' : Math.abs(videoStatus?.focus ?? 0) % 100}
      />
    </VStack>
  );
}
